// safety/safety-walks/pages/SafetyWalkFormPage.tsx
// New Safety Walk — dedicated page (Miro: New Safety Walk screen).
// Both the dashboard and register CTAs redirect here. Walk number is
// auto-generated server-side (SW-YYYY-NNN). On success, navigates to the
// new walk's detail page.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import { projectsApi } from '../../../../utils/lists-api';
import api from '../../../../utils/api';
import { createWalk } from '../../../../utils/safety-walks-api';
import { WalkFormFields } from '../components/WalkFormFields';
import type { SelectionUser, ProjectRef, WalkFormValue } from '../components/WalkFormFields';
import type { ParticipantRef } from '../../../../types/safety-walk.types';

export const SafetyWalkFormPage: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const [project, setProject] = useState('');
    const [location, setLocation] = useState('');
    const [walkDate, setWalkDate] = useState(new Date().toISOString().slice(0, 10));
    const [conductedBy, setConductedBy] = useState('');
    const [participants, setParticipants] = useState<ParticipantRef[]>([]);
    const [generalComments, setGeneralComments] = useState('');
    const [projects, setProjects] = useState<ProjectRef[]>([]);
    const [users, setUsers] = useState<SelectionUser[]>([]);
    const [saving, setSaving] = useState(false);

    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.SAFETY_WALK);

    useEffect(() => {
        projectsApi
            .list({ status: 'active' })
            .then((rows) => setProjects((rows || []).map((p) => ({ id: p.id, projectName: p.projectName, siteAddress: p.siteAddress }))))
            .catch(() => {});
        api.get('/api/users/selection')
            .then((res) => {
                if (res.success && Array.isArray(res.users)) setUsers(res.users);
            })
            .catch(() => {});
    }, []);

    const setForm = (patch: Partial<WalkFormValue>) => {
        if (patch.project !== undefined) setProject(patch.project);
        if (patch.location !== undefined) setLocation(patch.location);
        if (patch.walkDate !== undefined) setWalkDate(patch.walkDate);
        if (patch.conductedBy !== undefined) setConductedBy(patch.conductedBy);
        if (patch.participants !== undefined) setParticipants(patch.participants);
        if (patch.generalComments !== undefined) setGeneralComments(patch.generalComments);
    };

    const submit = async () => {
        if (!project) return toast.error('Project is required');
        if (!walkDate) return toast.error('Walk date is required');
        if (!conductedBy) return toast.error('Walk conducted by is required');

        setSaving(true);
        try {
            const selectedProject = projects.find((p) => p.projectName === project);
            const conductor = users.find((u) => String(u.id) === conductedBy);
            const res = await createWalk({
                project,
                location: location || (selectedProject ? selectedProject.siteAddress : undefined),
                walkDate,
                conductedBy: conductor ? conductor.name : (conductedBy.startsWith('name:') ? conductedBy.slice(5) : conductedBy),
                conductedById: conductor ? conductor.id : null,
                participants,
                generalComments: generalComments.trim() || undefined,
            });
            toast.success('Safety walk created');
            navigate(`/safety/walks/${res.walk.id}`);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to create safety walk');
        } finally {
            setSaving(false);
        }
    };

    if (!canCreate) {
        return <AccessRestricted accessMessage="You don't have permission to create safety walks." />;
    }

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-2xl mx-auto w-full">
            <button onClick={() => navigate('/safety/walks/register')} className="text-sm text-primary hover:underline mb-4 inline-flex items-center gap-1">
                <ChevronLeft size={14} /> Safety Walk Register
            </button>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="border-b border-border px-6 py-5">
                    <h1 className="text-xl font-semibold">New Safety Walk</h1>
                    <p className="text-sm text-muted-foreground">Walk Number: Auto Generated (SW-YYYY-NNN)</p>
                </div>

                <div className="p-6 space-y-5">
                    <WalkFormFields
                        value={{ project, location, walkDate, conductedBy, participants, generalComments }}
                        onChange={setForm}
                        projects={projects}
                        users={users}
                    />
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
                    <button onClick={() => navigate('/safety/walks/register')} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        Create Safety Walk
                    </button>
                </div>
            </div>
        </div>
    );
};
